// client-factory.js - фабрика для создания клиентского приложения
import { markRaw, nextTick, createApp as createVueApp, createSSRApp as createVueSSRApp } from 'vue';

// Стор запроса — внешняя граница приложения: createStore зовётся до построения
// приложения на сервере, useStore на клиенте отдаёт стор-синглтон вкладки.
import { createStore, getAsyncLocalStorage, useStore } from '../store/core.store.js';
// Словарь core регистрируется до getLocales(): инстанс vue-i18n собирается из
// кэша менеджера один раз, а core.initialize идёт после него. Без этого на
// клиенте и на первом SSR-запросе ключи `core.*` (Sidebar: core.ui.darkMode)
// печатались как есть, и гидрация давала mismatch с сервером, у которого
// кэш уже был наполнен прошлым запросом.
import { i18nManager } from './i18n.manager.js';
import coreLocales from '../../locales/index.js';

// Один и тот же файл выполняется в node (SSR) и в браузере.
const isServer = () => typeof window === 'undefined';

export function createUniversalApp({
  getConfig,
  getRouter,
  getLocales,
  getHooks = {}
}) {
  // Lazy imports to avoid circular dependencies
  const getLayoutApp = () => import('../components/layouts/App.vue').then(m => m.default);
  const getModuleRegistry = () => import('./module.manager.js').then(m => m.moduleManager);
  const getWebsockets = () => import('./ws.manager.js').then(m => m.default);
  const getAppRenderer = () => import('../utils/vue-app-renderer.js');
  
  // Асинхронная инициализация для избежания циклических зависимостей
  async function initializeApp() {
    // [LOADING 14] Starting initializeApp
    performance.mark('loading-14-start');
    console.log('[LOADING 14] Starting application initialization...');

    // Создаем конфигурацию
    const config = await getConfig();
    const hooks = getHooks;

    // unhead 3 развёл createHead по платформам: в браузере он патчит DOM,
    // на сервере рендерит теги в строку. Общей точки входа больше нет.
    const { createHead } = isServer()
      ? await import('@unhead/vue/server')
      : await import('@unhead/vue/client');

    // [LOADING 15] Loading core dependencies
    performance.mark('loading-15-start');
    console.log('[LOADING 15] Loading core dependencies via Promise.all...');

    const [layoutApp, moduleManager, wsManager, appRenderer] = await Promise.all([
      getLayoutApp(),
      getModuleRegistry(),
      getWebsockets(),
      getAppRenderer()
    ]);

    performance.mark('loading-15-end');
    performance.measure('loading-15', 'loading-15-start', 'loading-15-end');
    const measure15 = performance.getEntriesByName('loading-15')[0];
    console.log(`[LOADING 15] Core dependencies loaded in ${measure15?.duration?.toFixed(2)}ms`);
    
    // Core module is always loaded - no need to register in moduleManager

    // [LOADING 16] Registering modules in registry
    performance.mark('loading-16-start');
    console.log('[LOADING 16] Registering modules in registry...');

    // Регистрация модулей из config
    Object.entries(config.modules).forEach(([name, module]) => {
      if (module.loader) {
        moduleManager.register(name, {
          loader: module.loader,
          dependencies: module.dependencies || [],
          contexts: module.contexts,
          // Произвольный модуль-специфичный конфиг (например analytics.settings.adapters)
          settings: module.settings || {},
        });
      }
    });

    performance.mark('loading-16-end');
    performance.measure('loading-16', 'loading-16-start', 'loading-16-end');
    const measure16 = performance.getEntriesByName('loading-16')[0];
    console.log(`[LOADING 16] Modules registered in ${measure16?.duration?.toFixed(2)}ms`);
    
    // Основная функция создания приложения
    // Стор приходит снаружи: приложение строится уже внутри контекста запроса,
    // поэтому фабрика его не добывает, а получает готовым.
    function createApp(store) {
      const app = process.env.MOBILE_APP 
        ? createVueApp(layoutApp, config) 
        : createVueSSRApp(layoutApp, config);
      
      // disableDefaults: unhead 3 при SSR сам вставляет charset, viewport и
      // lang. Свои charset и viewport (viewport-fit=cover, user-scalable=no)
      // уже стоят в шаблоне страницы (builder/templates/page.js) — с дефолтами
      // в head попадали бы два разных viewport.
      const meta = isServer() ? createHead({ disableDefaults: true }) : createHead();
      i18nManager.register('core', coreLocales);
      const i18n = getLocales();

      // Инстанс vue-i18n едет в контексте запроса, а не в i18nManager: менеджер
      // один на процесс, а createApp выполняется на каждый SSR-запрос. Общий
      // инстанс в менеджере отдавал бы сообщения и язык одного запроса другому.
      const context = {
        app,
        store,
        router: null,
        config,
        i18n,
      };
      
      // Создаем роутер с контекстом
      const router = getRouter(context);
      context.router = router;

      // Контекст запроса (app, router, config) достижим через useStore() из любого
      // кода без provide. Стор per-request на сервере (AsyncLocalStorage) и синглтон
      // на клиенте, поэтому утечки контекста между запросами нет.
      // markRaw: контекст — не состояние, а носитель запроса. Без него стор
      // (reactive) заворачивает его в прокси, а прокси разворачивает ref-ы:
      // store.context.i18n.global.locale.value отдавал бы undefined.
      store.context = markRaw(context);

      // Регистрируем заглушки для роутов, которые требуют другие модули
      // Когда Organizations/Backoffice/Auth загрузятся, они заменят эти заглушки
      const stubComponent = { template: '<router-view />' };

      router.addRoute('Home', {
        path: 'backoffice',
        name: 'Backoffice Root',
        children: [],
        component: stubComponent,
        meta: {
          sidebar_width_hidden: 'w-0',
        }
      });

      router.addRoute('Home', {
        path: 'organizations/:_id',
        name: 'OrganizationRoot',
        children: [],
        component: stubComponent
      });

      router.addRoute('Home', {
        path: 'users/:_id',
        name: 'User Profile Root',
        children: [],
        component: stubComponent
      });
      
      // Маршруты всех модулей объявлены на старте (registerAll → register →
      // addRoutes), поэтому резолвить маршрут заново не нужно: активируем модули
      // записи маршрута (meta.modules) перед показом страницы.
      // Работает и на SSR — внутри router.push(url).
      router.beforeResolve(async (to) => {
        await moduleManager.activateRoute(to, context);
      });

      router.beforeResolve(async (to, from) => {
        store.core.state.loading = false;
      })
      
      // Error handler для lazy loaded chunks
      if (typeof window !== 'undefined') {
        window.addEventListener('error', (e) => {
          if (e.message && e.message.includes('Loading chunk')) {
            console.error('Chunk loading failed:', e);
            // Можно показать уведомление пользователю
            // или попытаться перезагрузить
          }
        });
      }
      
      // Инициализация в правильном порядке
      const initialize = async () => {
        // Хук ДО инициализации
        if (hooks.beforeInitialize) {
          await hooks.beforeInitialize(context);
        }
        // [LOADING 17] Initializing Core module
        performance.mark('loading-17-start');
        console.log('[LOADING 17] Initializing Core module...');

        // Core module must ALWAYS be initialized
        const ModuleCore = await import('../../core.client.js');
        await ModuleCore.default.initialize(app, store, router, config);

        performance.mark('loading-17-end');
        performance.measure('loading-17', 'loading-17-start', 'loading-17-end');
        const measure17 = performance.getEntriesByName('loading-17')[0];
        console.log(`[LOADING 17] Core module initialized in ${measure17?.duration?.toFixed(2)}ms`);

        // Маршруты всех модулей — на старте. ПОСЛЕ core.initialize: register()
        // модуля может опираться на провайды и глобальные миксины, которые
        // core ставит на app. Активация (initialize + локаль) — отдельно, по
        // записи маршрута или activateModule.
        await moduleManager.registerAll(context);

        // 2. WebSocket (отложим после гидратации, только если включен)
        const useWebsocket = config.globals?.websocket !== false; // по умолчанию false

        if (useWebsocket && typeof window !== 'undefined') {
          // Откладываем инициализацию WebSocket после гидратации
          const _ric = typeof requestIdleCallback !== 'undefined' ? requestIdleCallback : (cb) => setTimeout(cb, 1);
          _ric(() => {
            wsManager.initialize({
              wsUrl: process.env.WSS_URL || undefined,
              maxReconnectAttempts: 5,
              reconnectDelay: 3000,
              pingInterval: 30000,
            });
          });
        }

        // 5. Подключаем Vue плагины
        // Router подключается здесь только для MOBILE_APP и SSR server.
        // Для SSR client entry, router подключается в renderAndMountApp ПОСЛЕ гидратации state,
        // чтобы route guards имели доступ к auth state.
        if (process.env.MOBILE_APP || typeof window === 'undefined') {
          app.use(router);
        }
        app.use(i18n).use(meta);

        // 6. Монтируем приложение
        if (process.env.MOBILE_APP) {
          app.mount('#app');
          // Без SSR гидрации нет: всё после mount — переходы внутри SPA
          // (см. store.core.state.hydrated).
          store.core.state.hydrated = true;
        }

        // Хук ПОСЛЕ инициализации
        if (hooks.afterInitialize) {
          await hooks.afterInitialize(context);
        }

        // [LOADING 19] Application initialization completed
        performance.mark('loading-19-end');
        performance.measure('loading-19', 'loading-14-start', 'loading-19-end');
        const measure19 = performance.getEntriesByName('loading-19')[0];
        console.log(`[LOADING 19] Application initialization completed in ${measure19?.duration?.toFixed(2)}ms`);

        return {
          app,
          router,
          store,
          i18n,
          meta,
          moduleManager,
          config,
        };
      };
      
      // Возвращаем промис для SSR
      if (!process.env.MOBILE_APP) {
        return initialize();
      }
      
      // Для SPA инициализируем сразу
      initialize();
    }
    
    // FOR SSR / SERVER ENTRY
    async function renderApp({ url, cookies, headers, languages, ssrContext }) {
      // Порядок обязателен: сначала стор запроса, потом вход в контекст, и только
      // внутри контекста — построение приложения. Иначе всё, что поднимается в
      // хуке afterInitialize (активация модулей auth и analytics), кладёт своё
      // состояние мимо стора запроса, и SSR отдаёт страницу без авторизации.
      // Используем asyncLocalStorage.run() вместо enterWith() —
      // enterWith теряет контекст внутри renderToString
      if (typeof window === 'undefined') {
        const store = createStore();
        const als = await getAsyncLocalStorage();

        return als.run(store, async () => {
          const { app, router, i18n, meta, moduleManager } = await createApp(store);
          return _renderApp({ url, cookies, headers, ssrContext, app, router, store, i18n, meta, moduleManager });
        });
      }

      const { app, router, store, i18n, meta, moduleManager } = await createApp(useStore());

      return _renderApp({ url, cookies, headers, ssrContext, app, router, store, i18n, meta, moduleManager });
    }

    async function _renderApp({ url, cookies, headers, ssrContext, app, router, store, i18n, meta, moduleManager }) {
      const context = {
        app,
        store,
        router,
        config,
        i18n,
        ssr: true,
      };

      // [LOADING 20] SSR renderApp starting
      performance.mark('loading-20-start');
      console.log('[SSR-404] renderApp starting, url:', url);

      // Модули корневой записи маршрута (meta.modules у Home: auth, analytics)
      // активируем ДО навигации: auth нужен и гардам маршрутов, и optimistic-
      // гидрации из куки ниже, а она идёт до router.push. Модули самого маршрута
      // активирует beforeResolve внутри router.push().
      await moduleManager.activateRoute(router.resolve('/'), context);

      performance.mark('loading-20-end');
      performance.measure('loading-20', 'loading-20-start', 'loading-20-end');
      const measure20 = performance.getEntriesByName('loading-20')[0];
      console.log(`[LOADING 20] SSR root-route modules activated in ${measure20?.duration?.toFixed(2)}ms`);

      // Инициализируем auth ДО навигации, чтобы гарды на роутах имели доступ к auth state.
      // Optimistic-гидрация auth из куки: initialize() на сервере не ходит в сеть
      // (никаких HTTP-запросов к самому себе), поэтому не падает и не сбрасывает
      // авторизацию при поднимающейся базе. Ревалидация — на клиенте, фоном.
      // Сбрасывать нечего: экземпляр стора auth — свой на каждый запрос (его
      // создаёт фабрика из defineStore, см. views/store/core.store.js), так
      // что анонимный запрос стартует с пустой личности и чужой accessToken в
      // data-state не попадает.
      if (store.auth?.actions) {
        let user = null;
        if (cookies?.user) {
          try {
            user = typeof cookies.user === 'string' ? JSON.parse(cookies.user) : cookies.user;
          } catch (e) {
            console.error('[SSR] Failed to parse user cookie:', e);
            user = null;
          }
        }

        if (user) {
          try {
            await store.auth.actions.initialize(user);
          } catch (e) {
            console.error('[SSR] Failed to initialize auth before navigation:', e);
          }
        }
      }

      // Маршруты уже объявлены (registerAll на старте), выполняем навигацию:
      // beforeResolve активирует модули записи маршрута
      console.log('[SSR-404] pushing url:', url);
      console.log('[SSR-404] available routes:', router.getRoutes().map(r => r.path));
      await router.push(url);
      await router.isReady();
      console.log('[SSR-404] router ready, currentRoute:', router.currentRoute.value.name, router.currentRoute.value.path);

      const result = await appRenderer.render({
        url,
        cookies,
        headers,
        createApp: () => ({ app, router, store, i18n, meta }),
        ssrContext
      });

      if (ssrContext?.modules) {
        result.usedModules = Array.from(ssrContext.modules);
      }

      // Модули маршрута, их зависимости и подключённые через activateModule в этом
      // запросе: клиент активирует этот список до setInitialState, иначе часть
      // сторов не существует в момент гидратации.
      result.loadedModules = [...store.activated];

      // [LOADING 21] SSR render completed
      performance.mark('loading-21-end');
      performance.measure('loading-21', 'loading-20-start', 'loading-21-end');
      const measure21 = performance.getEntriesByName('loading-21')[0];
      console.log(`[LOADING 21] SSR render completed in ${measure21?.duration?.toFixed(2)}ms`);

      return result;
    }
    
    // FOR SPA
    if (typeof window !== 'undefined' && process.env.MOBILE_APP) {
      createApp(useStore());
    }
    
    // FOR SSR / CLIENT ENTRY
    if (typeof window !== 'undefined' && !process.env.MOBILE_APP) {
      // Используем renderAndMountApp для правильной гидратации со state
      appRenderer.renderAndMountApp({ createApp, hooks }).then(({ app, router, store, moduleManager }) => {
        // Хук ПОСЛЕ гидратации
        if (hooks.afterHydration) {
          hooks.afterHydration({ app, router, store, moduleManager });
        }
        
        // Модули уже загружены в renderAndMountApp, просто ставим маркер
        if (typeof window !== 'undefined') {
          window.performance.mark('client-ready');
        }
      }).catch(error => {
        console.error('Hydration failed:', error);
      });
    }
    
    return { createApp, renderApp };
  }
  
  // Возвращаем промис, который резолвится с функциями
  return initializeApp();
}